import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { SectionLabel } from '@perch/ui';

const TRACK_EMAIL = 'track@perchtravel.com';

interface EmptyDashboardProps {
  onAddManually: () => void;
}

/**
 * Empty state for the dashboard when user has 0 bookings.
 * "Your Concierge Is Ready" — warm invitation + anonymized social proof.
 *
 * The social proof card is a mini rebook card — same visual language as the
 * actual rebook celebration card the user will eventually see. This creates
 * visual foreshadowing: "this is what YOUR card will look like."
 */
export function EmptyDashboard({ onAddManually }: EmptyDashboardProps) {
  const { copied, copy: copyEmail } = useCopyToClipboard(TRACK_EMAIL);

  return (
    <div className="text-center py-16">
      {/* Concierge headline */}
      <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 dark:text-stone-100 mb-2">
        Your Concierge Is Ready
      </h3>
      <p className="text-stone-500 dark:text-stone-400 mb-8 max-w-sm mx-auto">
        Forward your next booking. We'll take it from here.
      </p>

      {/* Social proof: mini rebook card (visual foreshadowing) */}
      <div className="rounded-2xl border border-stone-200/60 dark:border-stone-700/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-4 max-w-xs mx-auto mb-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-600/60 dark:text-emerald-400/50 mb-2">
          Last month, a Perch user saved
        </div>
        <div className="text-3xl font-data tabular-nums text-emerald-600 dark:text-emerald-400 mb-1">
          $127
        </div>
        <div className="text-sm font-mono text-emerald-700/60 dark:text-emerald-300/50">
          SFO → NRT
        </div>
      </div>

      {/* Email card */}
      <div className="rounded-2xl border-2 border-stone-900 dark:border-stone-100 p-5 max-w-sm mx-auto mb-6">
        <SectionLabel className="mb-3">Forward Emails To</SectionLabel>
        <div className="flex items-center gap-3">
          <code className="flex-1 text-base font-mono text-stone-900 dark:text-stone-100 text-left">
            {TRACK_EMAIL}
          </code>
          <button
            onClick={copyEmail}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-[transform,opacity,background-color] ${
              copied
                ? 'bg-emerald-500 text-white'
                : 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90'
            }`}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-700">
          <p className="text-sm font-medium text-stone-700 dark:text-stone-300 text-left">
            Works with Delta, United, and American.
          </p>
          <p className="text-xs text-stone-400 dark:text-stone-500 mt-1 text-left">
            More airlines on the way.
          </p>
        </div>
      </div>

      <button
        onClick={onAddManually}
        className="text-sm text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
      >
        or add manually →
      </button>
    </div>
  );
}
